// src/userPanel/UserPanelLoader.js
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { obtenerUsuarioPorId, obtenerObjetosPorIdUsuario } from "../../services/api";
import nfcAuthService from "../../services/nfcAuthService";
import UserPanel from "../UserPanel";

import PinScreen from ".././components/PinScreen";
/**
 * Convierte un Firestore Timestamp a Date, si es que viene con la forma:
 * { _seconds: number, _nanoseconds: number }
 */
function parseFirestoreTimestamp(ts) {
  if (!ts) return null;

  // Checa si es un objeto con _seconds/_nanoseconds
  if (
    typeof ts === "object" &&
    ts !== null &&
    "_seconds" in ts &&
    "_nanoseconds" in ts
  ) {
    return new Date(ts._seconds * 1000 + ts._nanoseconds / 1e6);
  }

  // Si ya es string o algo distinto, intenta Date normal
  return new Date(ts);
}

export default function UserPanelLoader() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // MODIFICADO: Intentar obtener el ID del usuario de múltiples fuentes
  const docIdFromUrl = searchParams.get("doc"); // ?doc=XYZ en la URL
  const tokenFromUrl = searchParams.get("token"); // NUEVO: ?token=JWT para magic links
  
  const [usuario, setUsuario] = useState(null);
  const [objetos, setObjetos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [errorUsuario, setErrorUsuario] = useState("");
  const [errorObjetos, setErrorObjetos] = useState("");

  // ✅ NUEVOS ESTADOS PARA PIN
  const [pinRequired, setPinRequired] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);
  const [checkingPin, setCheckingPin] = useState(true);

  // NUEVO: Procesar Magic Link si viene con token
  useEffect(() => {
    if (docIdFromUrl && tokenFromUrl) {
      try {
        // Verificación básica del token (sin validar firma)
        const payload = JSON.parse(atob(tokenFromUrl.split('.')[1]));
        
        // Verificar si expiró
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          setErrorUsuario('Tu enlace ha expirado. Por favor solicita uno nuevo.');
          setCargando(false);
          setCheckingPin(false);
          return;
        }
        
        // ✅ NUEVO: Detectar bypassPin en el JWT
        if (payload.bypassPin === true) {
          sessionStorage.setItem('pinBypassed', 'true');
          setPinVerified(true);
          setPinRequired(false);
        }
        
        localStorage.setItem('nfcToken', tokenFromUrl);
        localStorage.setItem('loginMethod', 'magicLink');
        localStorage.setItem('userId', docIdFromUrl);
        
        if (payload.userId && payload.correo) {
          localStorage.setItem('userData', JSON.stringify({
            id: payload.userId,
            correoPropietario: payload.correo
          }));
        }

        // Limpiar URL para que no se vea el token
        window.history.replaceState({}, document.title, '/userpanel');
        
      } catch (error) {
        console.error('❌ Error procesando Magic Link:', error);
        setErrorUsuario('El enlace no es válido. Por favor solicita uno nuevo.');
        setCargando(false);
        setCheckingPin(false);
      }
    }
  }, [docIdFromUrl, tokenFromUrl]);

  // Obtener datos del usuario después de procesar magic link
  const userDataFromToken = nfcAuthService.getUserData();
  const docIdFromToken = userDataFromToken?.id;
  
  // Priorizar el token sobre la URL
  const docId = docIdFromToken || docIdFromUrl;

  // NUEVO: Manejar expiración de token durante la sesión
  useEffect(() => {
    if (nfcAuthService.isAuthenticated()) {
      const token = localStorage.getItem('nfcToken');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.exp && payload.exp * 1000 < Date.now()) {
            // Token expiró durante la sesión
            nfcAuthService.logout();
            setErrorUsuario('Tu sesión ha expirado. Por favor inicia sesión nuevamente.');
            setCargando(false);
            setCheckingPin(false);
          }
        } catch (e) {
          // Token malformado
          console.error('Token inválido:', e);
        }
      }
    }
  }, []);

  // ✅ NUEVO: Verificar estado del PIN después de cargar usuario
  useEffect(() => {
    async function checkPinStatus() {
      if (!docId || errorUsuario) {
        setCheckingPin(false);
        return;
      }

      // Si ya hay bypass activo, no verificar PIN
      const bypassed = sessionStorage.getItem('pinBypassed');
      if (bypassed === 'true') {
        setPinRequired(false);
        setPinVerified(true);
        setCheckingPin(false);
        return;
      }

      // ✅ NUEVO: Si el PIN ya fue validado en esta sesión, no volver a pedir
      const alreadyValidated = sessionStorage.getItem('pinValidated');
      if (alreadyValidated === 'true') {
        setPinRequired(false);
        setPinVerified(true);
        setCheckingPin(false);
        return;
      }

      try {
        const token = localStorage.getItem('nfcToken');
        if (!token) {
          setCheckingPin(false);
          return;
        }

        // Verificar si el usuario tiene PIN activo
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/usuarios/${docId}/pin/estado`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setPinRequired(data.pinActivo || false);
        } else {
          console.warn('[PIN] No se pudo verificar estado del PIN');
        }
      } catch (error) {
        console.error('[PIN] Error verificando estado:', error);
      } finally {
        setCheckingPin(false);
      }
    }

    // Solo verificar PIN después de que termine de cargar
    if (!cargando && usuario) {
      checkPinStatus();
    }
  }, [docId, errorUsuario, cargando, usuario]);

  useEffect(() => {
    async function fetchData() {
      if (errorUsuario) {
        return; // Solo sale si ya existe un error
      }

      if (!docId) {
        // Sin sesión activa: redirigir al login silenciosamente
        navigate('/login', { replace: true });
        return;
      }

      try {
        // 1) Obtener el usuario por ID
        const respUser = await obtenerUsuarioPorId(docId);
        if (respUser.error) {
          setErrorUsuario(respUser.error);
        } else {
          // ⭐ CORRECCIÓN IMPORTANTE: Agregar el token y asegurar que todas las propiedades necesarias estén presentes
          const token = localStorage.getItem('nfcToken') || tokenFromUrl;
          const usuarioCompleto = {
            ...respUser,
            token: token, // Agregar el token
            ownerId: respUser.ownerId || respUser.id || docId, // Asegurar que ownerId exista
            // Asegurar que otras propiedades críticas también estén presentes
            nombrePropietario: respUser.nombrePropietario || respUser.nombre || 'Usuario',
            correoVerificado: respUser.correoVerificado !== undefined ? respUser.correoVerificado : true,
            correoPropietario: respUser.correoPropietario || respUser.correo || '',
            urlAvatar: respUser.urlAvatar || null
          };
          
          setUsuario(usuarioCompleto); 
        }

        // 2) Obtener los objetos pertenecientes a ese usuario
        const respObjs = await obtenerObjetosPorIdUsuario(docId);
        if (respObjs.error) {
          setErrorObjetos(respObjs.error);
        } else {
          // respObjs.data es el array de objetos
          const objetosRaw = respObjs.data || [];

          // Convertir fechaActivacion (y cualquier otro timestamp) a Date
          const objetosParseados = objetosRaw.map((obj) => {
            // Clonamos el objeto
            const newObj = { ...obj };

            // Si existe fechaActivacion en formato Firestore, parsearla
            if (newObj.fechaActivacion) {
              newObj.fechaActivacion = parseFirestoreTimestamp(
                newObj.fechaActivacion
              );
            }

            // Asegurar que cada objeto tenga las propiedades mínimas necesarias
            newObj.id = newObj.id || obj.id;
            newObj.tipo = newObj.tipo || 'objeto';
            newObj.nombre = newObj.nombre || 'Sin nombre';
            newObj.plan = newObj.plan || 'light';
            newObj.extraviado = newObj.extraviado || false;
            newObj.bloqueado = newObj.bloqueado || false;

            return newObj;
          });

          setObjetos(objetosParseados);
        }
      } catch (err) {
        console.error("Error al cargar datos de usuario u objetos =>", err);
        setErrorUsuario("Error al cargar datos de usuario: " + err.message);
      } finally {
        setCargando(false);
      }
    }

    fetchData();
  }, [docId, errorUsuario, docIdFromUrl, tokenFromUrl]);

  // ✅ NUEVO: Loading mientras verifica PIN
  if (cargando || checkingPin) {
    return (
      <div className="container my-4">
        <h4>Cargando información...</h4>
      </div>
    );
  }

  if (errorUsuario) {
    // Errores de sesion/token: redirigir al login
    if (errorUsuario.includes('expirado') || errorUsuario.includes('sesión') || errorUsuario.includes('enlace')) {
      navigate('/login', { replace: true });
      return null;
    }
    return (
      <div className="container my-4">
        <h4>Error: {errorUsuario}</h4>
      </div>
    );
  }

  if (errorObjetos) {
    return (
      <div className="container my-4">
        <h4>Error en objetos: {errorObjetos}</h4>
      </div>
    );
  }

  // ✅ NUEVO: Mostrar PinScreen si es necesario
  if (pinRequired && !pinVerified) {
    return (
      <PinScreen
        onSuccess={() => {
          // ✅ Guardar en sessionStorage para no volver a pedir en esta sesión
          sessionStorage.setItem('pinValidated', 'true');
          setPinVerified(true);
        }}
        userId={docId}
      />
    );
  }

  // Renderiza el componente presentacional "UserPanel"
  return (
    <UserPanel
      usuario={usuario}
      objetos={objetos}
      cargando={cargando}
      errorUsuario={errorUsuario}
      errorObjetos={errorObjetos}
      setUsuario={setUsuario}
      setObjetos={setObjetos}
    />
  );
}